import type { settingTypes } from '@ipfabric/utils';

import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, type FormikProps, useFormik } from 'formik';
import { type FC, memo } from 'react';
import { validateSlug } from 'utils/formValidation/validations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { hasHttpOrHttps } from 'utils/hasHttpOrHtttps';
import { VendorDialogContainer } from './VendorDialogContainer';
import { FormikAdvancedSettings } from './fields/FormikAdvancedSettings';
import { FormikRateLimitingSettingsFields } from './fields/FormikRateLimitingSettingsFields';
import { FormikVendorApiBaseUrlsField } from './fields/FormikVendorApiBaseUrlsField';
import { FormikVendorApiCommentField } from './fields/FormikVendorApiCommentField';
import { FormikVendorApiEnabledField } from './fields/FormikVendorApiEnabledField';
import { FormikVendorApiRespectSystemProxyConfigurationField } from './fields/FormikVendorApiRejectUnauthorizedField';
import { FormikVendorApiRejectUnauthorizedField } from './fields/FormikVendorApiRespectSystemProxyConfigurationField';
import { FormikVendorApiSlugField } from './fields/FormikVendorApiSlugField';
import { FormikVendorApiUsernamePasswordFields } from './fields/FormikVendorApiUsernamePasswordFields';
import { getAdvancedSettingsDefaultValues } from './helpers/applyDefaultInitialValues';
import { isVendorUpdate } from './helpers/guardTypes';
import type { BaseVendorFormValues, VendorDialogProps } from './vendorTypes';

export type F5osCFormValues =
  BaseVendorFormValues<settingTypes.F5osCApiSettings>;

type Props = VendorDialogProps<
  F5osCFormValues,
  settingTypes.F5osCApiSettings,
  settingTypes.UpdateF5osCApiSettings
>;

const resetSecretAttributes = (values: F5osCFormValues): F5osCFormValues => {
  return {
    ...values,
    username: '',
    password: '',
  };
};

const BareF5osCForm: FC<Props> = (props) => {
  const editMode = props.dialogType === 'edit';
  const msg = useLocale();
  const formik = useFormik<F5osCFormValues>({
    initialValues: resetSecretAttributes(props.initialValues),
    validate: (values) => {
      const { required, baseUrlStartsWith } =
        msg.discoverySettings.vendorsApi.validations;
      const errors: FormikErrors<F5osCFormValues> = {};

      if (!values.baseUrls || !values.baseUrls.length) {
        errors.baseUrls = required;
      } else if (values.baseUrls.some((baseUrl) => !hasHttpOrHttps(baseUrl))) {
        errors.baseUrls = baseUrlStartsWith;
      }
      if (!editMode && !values.username) {
        errors.username = required;
      }
      if (!editMode && !values.password) {
        errors.password = required;
      }
      validateSlug(values, errors, msg);

      return errors;
    },
    onSubmit: makeFormikOnSubmit((values: F5osCFormValues) => {
      const formattedValues = {
        ...values,
        ...getAdvancedSettingsDefaultValues(values),
      };

      if (isVendorUpdate(values) && props.dialogType === 'edit') {
        props.onSubmit({
          ...formattedValues,
          id: values.id,
          username:
            values.username || props.initialValues.username || undefined,
          password:
            values.password || props.initialValues.password || undefined,
        });
      } else if (props.dialogType === 'add') {
        props.onSubmit(formattedValues);
      }
    }),
  });

  return (
    <VendorDialogContainer
      formik={formik as FormikProps<F5osCFormValues>}
      dialogType={props.dialogType}
      {...props.vendorDialogContainerPassthruProps}
    >
      <FormikVendorApiEnabledField formik={formik} />
      <FormikVendorApiUsernamePasswordFields
        formik={formik}
        isEditMode={editMode}
      />
      <FormikVendorApiBaseUrlsField formik={formik} />
      <FormikVendorApiSlugField formik={formik} />
      <FormikVendorApiCommentField formik={formik} />
      <FormikVendorApiRespectSystemProxyConfigurationField formik={formik} />
      <FormikVendorApiRejectUnauthorizedField formik={formik} />
      <FormikAdvancedSettings>
        <FormikRateLimitingSettingsFields formik={formik} />
      </FormikAdvancedSettings>
    </VendorDialogContainer>
  );
};

export const F5osCForm = memo(BareF5osCForm);
